import type { AgentExclusivePropertiesSection } from '~/components/Sections/AgentExclusivePropertiesSection';
import type { AgentHeroSection } from '~/components/Sections/AgentHeroSection';
import type { AgentPortfolioSection } from '~/components/Sections/AgentPortfolioSection';
import type { AgentShortsSection } from '~/components/Sections/AgentShortsSection';
import type { BrokerAboutSection } from '~/components/Sections/BrokerAboutSection';
import type { BrokerPerformanceSection } from '~/components/Sections/BrokerPerformanceSection';
import type { BrokerProfileCardSection } from '~/components/Sections/BrokerProfileCardSection';

const ELEMENT_NODE = 1;

interface SectionInstance {
	$el?: unknown;
	hasContent?: boolean;
}

function toElement(node: unknown): HTMLElement | null {
	if (!node || (node as Node).nodeType !== ELEMENT_NODE) return null;

	return node as HTMLElement;
}
function resolveRootElement(
	instance: SectionInstance | null
): HTMLElement | null {
	const node = instance?.$el as Node | null | undefined;

	if (!node) return null;

	const element = toElement(node);
	if (element) return element;

	let sibling = node.nextSibling;

	while (sibling && sibling.nodeType !== ELEMENT_NODE) {
		sibling = sibling.nextSibling;
	}

	return toElement(sibling);
}

function resolveSectionElement(
	instance: SectionInstance | null
): HTMLElement | null {
	if (!instance?.hasContent) return null;

	return toElement(instance.$el);
}

export const useAgentContextualMenu = (): void => {
	const heroSectionRef =
		useTemplateRef<InstanceType<typeof AgentHeroSection>>('heroSectionRef');
	const aboutRef =
		useTemplateRef<InstanceType<typeof BrokerAboutSection>>('aboutRef');
	const exclusivesRef =
		useTemplateRef<InstanceType<typeof AgentExclusivePropertiesSection>>(
			'exclusivesRef'
		);
	const performanceRef =
		useTemplateRef<InstanceType<typeof BrokerPerformanceSection>>(
			'performanceRef'
		);
	const shortsRef =
		useTemplateRef<InstanceType<typeof AgentShortsSection>>('shortsRef');
	const portfolioRef =
		useTemplateRef<InstanceType<typeof AgentPortfolioSection>>('portfolioRef');
	const profileCardRef =
		useTemplateRef<InstanceType<typeof BrokerProfileCardSection>>(
			'profileCardRef'
		);

	const heroSectionEl = computed(() =>
		resolveRootElement(heroSectionRef.value)
	);
	const aboutEl = computed(() => resolveSectionElement(aboutRef.value));
	const exclusivesEl = computed(() =>
		resolveSectionElement(exclusivesRef.value)
	);
	const performanceEl = computed(() =>
		resolveSectionElement(performanceRef.value)
	);
	const shortsEl = computed(() => resolveSectionElement(shortsRef.value));
	const portfolioEl = computed(() => resolveSectionElement(portfolioRef.value));
	const profileCardEl = computed(() =>
		resolveSectionElement(profileCardRef.value)
	);

	const sectionRefs = computed(() => [
		aboutEl,
		exclusivesEl,
		shortsEl,
		performanceEl,
		portfolioEl,
		profileCardEl
	]);

	useContextualHeaderVisibilityControl({
		heroSectionRef: heroSectionEl,
		sectionRefs,
		anchorSection: profileCardEl
	});
};
